/** The Construction Kit's elements, in palette order (docs/spec/03 §1 legend, 07 §4). */

export interface KitElement {
  /** The legend character. */
  readonly ch: string;
  /** Short name for the status line. */
  readonly name: string;
  /** One line for the status line when hovered. */
  readonly tip: string;
  /** Only the Deep set uses it (custom caves may use everything). */
  readonly deep: boolean;
}

function el(ch: string, name: string, tip: string, deep = false): KitElement {
  return { ch, name, tip, deep };
}

/** Three rows of eight: the ground, the cast, the Deep's additions. */
export const KIT_ELEMENTS: readonly KitElement[] = [
  el(' ', 'SPACE', 'EMPTY SPACE: THINGS FALL THROUGH IT'),
  el('.', 'DIRT', 'DIRT: DIGBY DIGS IT; IT HOLDS THINGS UP'),
  el('w', 'BRICK WALL', 'BRICK WALL: THINGS ROLL OFF; BLASTS BREAK IT'),
  el('m', 'MAGIC WALL', 'MAGIC WALL: BOULDERS DROPPED THROUGH BECOME DIAMONDS'),
  el('W', 'STEEL WALL', 'STEEL WALL: NOTHING BREAKS IT'),
  el('r', 'BOULDER', 'BOULDER: FALLS, ROLLS, CAN BE PUSHED'),
  el('d', 'DIAMOND', 'DIAMOND: COLLECT THE QUOTA TO OPEN THE EXIT'),
  el('?', 'RANDOM', 'RANDOM FILL: ROLLED FROM THE FILL SETTINGS BELOW'),
  el('P', 'INBOX', 'INBOX: DIGBY STARTS HERE (ONLY ONE)'),
  el('X', 'EXIT', 'EXIT: OPENS WHEN THE QUOTA IS MET'),
  el('f', 'FIREFLY ◄', 'FIREFLY, SETTING OFF LEFT: EXPLODES WHEN HIT'),
  el('F', 'FIREFLY ►', 'FIREFLY, SETTING OFF RIGHT: EXPLODES WHEN HIT'),
  el('b', 'BUTTERFLY ▼', 'BUTTERFLY, SETTING OFF DOWN: EXPLODES INTO DIAMONDS'),
  el('B', 'BUTTERFLY ▲', 'BUTTERFLY, SETTING OFF UP: EXPLODES INTO DIAMONDS'),
  el('a', 'AMOEBA', 'AMOEBA: GROWS; SEALED IN IT TURNS TO DIAMONDS'),
  el('t', 'CLOCK', 'CLOCK (DEEP): ADDS TEN SECONDS', true),
  el('x', 'EXPANDING WALL', 'EXPANDING WALL (DEEP): GROWS SIDEWAYS INTO SPACE', true),
  el('s', 'SLIME', 'SLIME (DEEP): THINGS DRIP THROUGH IT NOW AND THEN', true),
  el('1', 'GOLD KEY', 'GOLD KEY (DEEP): OPENS EVERY GOLD DOOR', true),
  el('2', 'JADE KEY', 'JADE KEY (DEEP): OPENS EVERY JADE DOOR', true),
  el('3', 'RUBY KEY', 'RUBY KEY (DEEP): OPENS EVERY RUBY DOOR', true),
  el('4', 'GOLD DOOR', 'GOLD DOOR (DEEP): SOLID UNTIL THE GOLD KEY IS TAKEN', true),
  el('5', 'JADE DOOR', 'JADE DOOR (DEEP): SOLID UNTIL THE JADE KEY IS TAKEN', true),
  el('6', 'RUBY DOOR', 'RUBY DOOR (DEEP): SOLID UNTIL THE RUBY KEY IS TAKEN', true),
];

const BY_CHAR = new Map(KIT_ELEMENTS.map((e) => [e.ch, e]));

export function kitElement(ch: string): KitElement | undefined {
  return BY_CHAR.get(ch);
}

/** The element's name, or a marker for a character the kit does not know. */
export function elementName(ch: string): string {
  return BY_CHAR.get(ch)?.name ?? `UNKNOWN ${JSON.stringify(ch)}`;
}

/** The palette index of `ch`, or -1. */
export function paletteIndex(ch: string): number {
  return KIT_ELEMENTS.findIndex((e) => e.ch === ch);
}

/** What the random fill may produce: anything but another `?` or a second inbox. */
export function canFill(ch: string): boolean {
  return BY_CHAR.has(ch) && ch !== '?' && ch !== 'P';
}
